import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../api/auth';

export default function Login() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = mode === 'login'
        ? await api.login(form.email, form.password)
        : await api.signup(form.email, form.password, form.name);
      login(data);
      nav('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="calliope-login-page" style={S.page}>
      <style>{`
        @media (max-width: 768px) {
          .calliope-login-page { flex-direction: column; }
          .calliope-login-left { padding: 32px 24px !important; flex: none !important; }
          .calliope-login-left-content { padding-top: 24px !important; }
          .calliope-login-stats { flex-wrap: wrap; gap: 20px !important; }
          .calliope-login-right { padding: 32px 20px !important; flex: none !important; }
        }
      `}</style>
      <div className="calliope-login-left" style={S.left}>
        <div style={S.brand} onClick={() => nav('/')}>🎙️ <span style={S.brandText}>Calliope</span></div>
        <div className="calliope-login-left-content" style={S.leftContent}>
          <div style={S.leftHeadline}>Practice your pitch.<br />Nail the real thing.</div>
          <div style={S.leftSub}>AI cold call training with realistic personas, live scoring, and coaching benchmarked against top performers.</div>
          <div className="calliope-login-stats" style={S.leftStats}>
            {[['3 free calls', 'no credit card needed'], ['11 personas', 'ready to use'], ['6 dimensions', 'scored per call']].map(([v, l], i) => (
              <div key={i} style={S.leftStat}>
                <div style={S.leftStatVal}>{v}</div>
                <div style={S.leftStatLabel}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="calliope-login-right" style={S.right}>
        <div style={S.card}>
          <div style={S.cardTitle}>{mode === 'login' ? 'Welcome back' : 'Create your account'}</div>
          <div style={S.cardSub}>{mode === 'login' ? 'Sign in to continue practicing' : 'Start with 3 free practice calls'}</div>

          <div style={S.tabs}>
            {[['login', 'Sign In'], ['signup', 'Sign Up']].map(([m, label]) => (
              <button key={m} style={{ ...S.tab, ...(mode === m ? S.tabActive : {}) }} onClick={() => setMode(m)}>{label}</button>
            ))}
          </div>

          {mode === 'signup' && (
            <div style={{ fontSize: 13, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
              We're not taking new signups right now. If you were given login credentials, please use the Sign In tab instead.
            </div>
          )}

          <form onSubmit={submit}>
            {mode === 'signup' && (
              <div style={S.field}>
                <label style={S.label}>Full name</label>
                <input style={S.input} placeholder="Alex Johnson" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
            )}
            <div style={S.field}>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" placeholder="you@university.edu" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div style={S.field}>
              <label style={S.label}>Password</label>
              <input style={S.input} type="password" placeholder="••••••••" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
            </div>

            {error && <div style={S.error}>{error}</div>}

            <button style={{ ...S.btn, ...(mode === 'signup' ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }} type="submit" disabled={loading || mode === 'signup'}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Signups disabled'}
            </button>
          </form>

          {mode === 'signup' && <div style={S.fine}>By signing up you agree to our terms. 3 free calls per month, no credit card required.</div>}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', display: 'flex', fontFamily: 'Inter, -apple-system, sans-serif' },
  left: { flex: '1 1 420px', background: '#1D4ED8', padding: '40px 48px', display: 'flex', flexDirection: 'column' },
  brand: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, cursor: 'pointer', marginBottom: 'auto' },
  brandText: { fontWeight: 700, color: '#fff' },
  leftContent: { marginBottom: 'auto', paddingTop: 60 },
  leftHeadline: { fontSize: 36, fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.02em', marginBottom: 16 },
  leftSub: { fontSize: 16, color: '#BFDBFE', lineHeight: 1.7, marginBottom: 40, maxWidth: 360 },
  leftStats: { display: 'flex', gap: 32 },
  leftStat: { display: 'flex', flexDirection: 'column' },
  leftStatVal: { fontSize: 20, fontWeight: 700, color: '#fff', fontFamily: 'JetBrains Mono, monospace' },
  leftStatLabel: { fontSize: 12, color: '#93C5FD', marginTop: 2 },
  right: { flex: '1 1 420px', background: '#F8F9FB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' },
  card: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
  cardSub: { fontSize: 14, color: '#9CA3AF', marginBottom: 24 },
  tabs: { display: 'flex', background: '#F1F3F7', borderRadius: 8, padding: 4, marginBottom: 24, gap: 4 },
  tab: { flex: 1, padding: '8px', background: 'transparent', border: 'none', borderRadius: 6, color: '#6B7280', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  tabActive: { background: '#fff', color: '#111827', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8, color: '#111827', fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  error: { fontSize: 13, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 12px', marginBottom: 16 },
  btn: { width: '100%', padding: '11px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: 'pointer', marginTop: 4 },
  fine: { fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 16, lineHeight: 1.5 },
};
